import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'wouter';
import { db } from '../lib/firebase';
import { createNotification } from '../lib/notifications';
import { doc, getDoc, getDocs, addDoc, collection, query, where, onSnapshot, updateDoc, orderBy, setDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { Building2, Users, LayoutDashboard, LogOut, Package, ClipboardList, Clock, CheckCircle2, ChevronRight, RotateCcw, Truck, Plus, Minus, AlertTriangle, History, Search, QrCode, X, ArrowLeft, Filter, Hand, ExternalLink, Edit2, Trash2, CreditCard, Calendar, MapPin, Mail, User, MessageSquare, Send, FileText, Archive, Bell, Settings, CircleHelp, Gauge, Share2, Printer, FileSpreadsheet, Layers, Download, CheckSquare, Square, Boxes, Check } from 'lucide-react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { cn } from '../lib/utils';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import Barcode from 'react-barcode';
import { useReactToPrint } from 'react-to-print';

// Component for the shipping label
const ShippingLabel = React.forwardRef(({ order, items }: { order: any, items: any[] }, ref: any) => {
  const totalQty = items?.reduce((acc, item) => acc + (Number(item.quantity) || 0), 0) || 0;

  return (
    <div ref={ref} className="bg-white text-black p-4 font-mono select-none" style={{ width: '4in', minHeight: '6in', boxSizing: 'border-box' }}>
      {/* Outer Border for nice visual layout simulation */}
      <div className="border-4 border-black p-3 h-full flex flex-col justify-between" style={{ minHeight: 'calc(6in - 2rem)' }}>
        
        {/* Header Block */}
        <div className="text-center border-b-4 border-black pb-2 mb-2">
          <h2 className="text-2xl font-black uppercase tracking-tight">AJJAS STORE</h2>
          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-700">Fulfillment Center Priority Shipping</p>
        </div>

        {/* Order Details Compartment */}
        <div className="grid grid-cols-2 gap-1 border-b-2 border-dashed border-black pb-2 mb-2 text-xs">
          <div>
            <span className="block text-[8px] font-bold uppercase text-slate-600">ORDER NO</span>
            <span className="font-extrabold text-sm">#{order?.orderNumber || order?.id?.slice(0, 8)}</span>
          </div>
          <div className="text-right">
            <span className="block text-[8px] font-bold uppercase text-slate-600">DATE</span>
            <span className="font-extrabold text-[10px]">
              {order?.createdAt?.toDate 
                ? format(order.createdAt.toDate(), 'yyyy-MM-dd HH:mm') 
                : order?.createdAt instanceof Date 
                  ? format(order.createdAt, 'yyyy-MM-dd HH:mm')
                  : format(new Date(), 'yyyy-MM-dd HH:mm')}
            </span>
          </div>
        </div>

        {/* Shipping Address Compartment */}
        <div className="border-b-4 border-black pb-3 mb-2">
          <span className="block text-[8px] font-bold uppercase text-slate-600 mb-0.5">SHIP TO:</span>
          <p className="text-sm font-black uppercase leading-tight">{order?.customerName}</p>
          <p className="text-[11px] font-bold mt-1 text-slate-800 leading-tight">
            {order?.customerAddress || 'Fulfillment Center Priority Delivery Address Zone 4'}
          </p>
          {order?.customerPhone && (
            <p className="text-[10px] font-bold mt-1 text-slate-700">PH: {order.customerPhone}</p>
          )}
        </div>

        {/* Barcode Block (Beautiful Monochrome Thermal look) */}
        <div className="flex flex-col items-center justify-center border-b-2 border-dashed border-black py-2 mb-2">
          <Barcode 
            value={order?.id?.slice(0, 10).toUpperCase() || 'AJJAS0000'} 
            width={1.6} 
            height={50} 
            fontSize={10}
            font="monospace" 
            background="transparent"
            lineColor="#000000"
            margin={0}
          />
        </div>

        {/* Items Compartment */}
        <div className="flex-1 min-h-[1.5in] flex flex-col justify-start mb-2">
          <div className="flex justify-between text-[8px] font-black uppercase border-b border-black pb-0.5 mb-1.5 text-slate-600">
            <span>ITEM DESCRIPTION</span>
            <span className="text-right">QTY</span>
          </div>
          <div className="space-y-1">
            {items.map((item, i) => (
              <div key={i} className="flex justify-between items-start text-[11px] font-extrabold leading-tight">
                <span className="flex-1 pr-2 break-all uppercase">
                  {item.name} <span className="text-[9px] font-normal text-slate-600">({item.selectedWeight || '100g'})</span>
                </span>
                <span className="text-right font-black font-mono select-all ml-2 whitespace-nowrap">
                  {item.quantity} {item.unit || 'unit'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* End of Label - Total Items Block */}
        <div className="border-t-4 border-black pt-2 flex justify-between items-center bg-black text-white p-2">
          <div className="text-left">
            <span className="block text-[9px] font-extrabold tracking-wider uppercase text-slate-300">TOTAL NUMBER OF ITEMS</span>
            <span className="text-lg font-black tracking-tight">{totalQty} UNITS</span>
          </div>
          <div className="text-right font-mono text-[8px] font-bold uppercase text-slate-300 text-right">
            TERMINAL {order?.facilityCenterId?.slice(-4).toUpperCase() || '01-A'}
          </div>
        </div>

      </div>
      
      {/* CSS rules specifically injected for perfect printing result */}
      <style>{`
        @media print {
          @page {
            size: 4in 6in;
            margin: 0;
          }
          body {
            background-color: #ffffff !important;
          }
        }
      `}</style>
    </div>
  );
});

// Component for the Thermal Shipping Receipt
const ShippingReceipt = React.forwardRef(({ order, items }: { order: any, items: any[] }, ref: any) => {
  const totalAmount = items?.reduce((acc, item) => {
    const itemPrice = Number(item.price) || 50; 
    const qty = Number(item.quantity) || 1;
    return acc + (itemPrice * qty);
  }, 0) || 0;

  const cashAmount = totalAmount > 0 ? (Math.ceil(totalAmount / 100) * 100) : 0;
  const changeAmount = totalAmount > 0 ? (cashAmount - totalAmount) : 0;

  const dateStr = order?.createdAt?.toDate 
    ? format(order.createdAt.toDate(), 'dd-MM-yyyy') 
    : order?.createdAt instanceof Date 
      ? format(order.createdAt, 'dd-MM-yyyy')
      : format(new Date(), 'dd-MM-yyyy');

  const timeStr = order?.createdAt?.toDate 
    ? format(order.createdAt.toDate(), 'hh.mm a') 
    : order?.createdAt instanceof Date 
      ? format(order.createdAt, 'hh.mm a')
      : format(new Date(), 'hh.mm a');

  return (
    <div ref={ref} className="bg-white text-black p-4 font-mono select-none text-[11px] leading-relaxed tracking-wider print:p-2" style={{ width: '3.1in', boxSizing: 'border-box' }}>
      {/* Sawtooth top border simulation */}
      <div className="text-center text-[8px] text-slate-400 select-none pb-1 font-sans">
        /\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\
      </div>

      <div className="text-center text-[9px] tracking-tight font-bold">
        ********************************************
      </div>
      <div className="text-center text-sm font-black tracking-widest py-0.5 uppercase">
        RECEIPT
      </div>
      <div className="text-center text-[9px] tracking-tight font-bold">
        ********************************************
      </div>

      <div className="text-center text-base font-black tracking-tight uppercase mt-1.5 mb-2">
        AJJAS MASALA
      </div>

      <div className="border-b border-dashed border-black my-1"></div>

      <div className="flex justify-between text-[11px] font-bold py-1">
        <span>DATE :- {dateStr}</span>
        <span>{timeStr.toUpperCase()}</span>
      </div>

      <div className="border-b border-dashed border-black my-1"></div>

      {/* Items List */}
      <div className="space-y-2 py-2">
        {items.map((item, i) => {
          const itemPrice = Number(item.price) || 50;
          return (
            <div key={i} className="flex justify-between items-start text-[11px] font-bold leading-tight">
              {/* Left Column: SKU */}
              <span className="w-16 shrink-0 text-left font-normal truncate uppercase text-[9px] text-slate-600">
                {item.sku || item.id?.slice(0, 8).toUpperCase() || '15-453574'}
              </span>
              
              {/* Middle Column: Name */}
              <span className="flex-1 text-left px-1 break-words">
                {item.name} {item.selectedWeight ? `(${item.selectedWeight})` : ''}
              </span>
              
              {/* Right Column: Price */}
              <span className="w-16 shrink-0 text-right font-mono font-black">
                ₹{(itemPrice * item.quantity).toFixed(2)}
              </span>
            </div>
          );
        })}
      </div>

      <div className="border-b border-dashed border-black my-1"></div>

      {/* Totals Block */}
      <div className="space-y-1 py-1 text-[11px]">
        <div className="flex justify-between font-black text-sm">
          <span>TOTAL</span>
          <span>₹{totalAmount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between font-bold">
          <span>CASH</span>
          <span>₹{cashAmount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between font-bold">
          <span>CHANGE</span>
          <span>₹{changeAmount.toFixed(2)}</span>
        </div>
      </div>

      <div className="border-b border-dashed border-black my-1"></div>

      <div className="text-center text-sm font-black tracking-widest py-3 uppercase">
        THANK YOU!
      </div>

      {/* Dotted Tear Line */}
      <div className="text-center text-[10px] text-slate-500 font-bold tracking-widest pb-3">
        ••••••••••••••••••••••••••••••••••••••••••••
      </div>

      {/* Barcode Centered */}
      <div className="flex flex-col items-center justify-center pt-1 pb-3">
        <Barcode 
          value={order?.orderNumber || order?.id?.slice(0, 10).toUpperCase() || 'AJJAS-SHIP'} 
          width={1.2} 
          height={35} 
          fontSize={8}
          font="monospace" 
          background="transparent"
          lineColor="#000000"
          margin={0}
          displayValue={true}
        />
      </div>

      {/* Sawtooth bottom border simulation */}
      <div className="text-center text-[8px] text-slate-400 select-none pt-1 font-sans">
        \/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\/\
      </div>

      <style>{`
        @media print {
          @page {
            size: 3.15in auto;
            margin: 0;
          }
          body {
            background-color: #ffffff !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>
    </div>
  );
});

// Component for the Invoice
const Invoice = React.forwardRef(({ order, items }: { order: any, items: any[] }, ref: any) => {
  return (
    <div ref={ref} className="p-10 bg-white w-full max-w-[8.5in] border border-slate-200 font-sans">
      <div className="flex justify-between items-start mb-12">
        <div>
          <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tighter mb-2">Invoice</h1>
          <div className="h-1 w-20 bg-emerald-500 rounded-full" />
        </div>
        <div className="text-right">
          <h2 className="text-xl font-black text-slate-900">SpiceOps Inc.</h2>
          <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">Order Fulfillment Network</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-10 mb-12">
        <div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Billed To</p>
          <p className="text-lg font-black text-slate-900">{order.customerName}</p>
          <p className="text-sm text-slate-500 mt-1">{order.customerAddress}</p>
        </div>
        <div className="text-right">
          <div className="inline-block text-left">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Order Details</p>
            <div className="space-y-1">
              <p className="text-sm font-bold text-slate-900 flex justify-between gap-10">ID: <span className="font-normal text-slate-500">#{order.orderNumber || order.id?.slice(0, 8)}</span></p>
              <p className="text-sm font-bold text-slate-900 flex justify-between gap-10">Date: <span className="font-normal text-slate-500">{order.createdAt?.toDate ? format(order.createdAt.toDate(), 'PPP') : 'N/A'}</span></p>
              <p className="text-sm font-bold text-slate-900 flex justify-between gap-10">Status: <span className="font-normal text-slate-500 uppercase">{order.status}</span></p>
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-hidden border border-slate-100 rounded-2xl mb-12">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest">
            <tr>
              <th className="px-6 py-4">Description</th>
              <th className="px-6 py-4 text-center">Qty</th>
              <th className="px-6 py-4 text-right">Unit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((item, i) => (
              <tr key={i}>
                <td className="px-6 py-4 font-bold text-slate-900">{item.name}</td>
                <td className="px-6 py-4 text-center font-medium text-slate-500">{item.quantity}</td>
                <td className="px-6 py-4 text-right font-medium text-slate-500 uppercase">{item.unit || 'units'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end pt-8 border-t border-slate-100">
        <div className="w-64 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="font-bold text-slate-400 uppercase">Subtotal Items</span>
            <span className="font-black text-slate-900">{items.reduce((acc, i) => acc + (Number(i.quantity) || 0), 0)}</span>
          </div>
          <div className="flex justify-between text-lg border-t border-slate-100 pt-3">
            <span className="font-black text-slate-900 uppercase">Total Items</span>
            <span className="font-black text-emerald-600">{items.reduce((acc, i) => acc + (Number(i.quantity) || 0), 0)}</span>
          </div>
        </div>
      </div>

      <div className="mt-20 text-[10px] font-bold text-slate-300 uppercase tracking-widest text-center">
        Thank you for choosing SpiceOps Distribution
      </div>

      <style>{`
        @media print {
          @page {
            size: auto;
            margin: 20mm;
          }
          body {
            background-color: #ffffff !important;
          }
        }
      `}</style>
    </div>
  );
});

// Helper function to extract net weight in grams from strings like "100g", "250g", "1kg", "500 gm"
function parseWeightInGrams(weightStr: string | undefined): number {
  if (!weightStr) return 100;
  const lower = String(weightStr).toLowerCase().trim();
  if (lower.includes('kg')) {
    const val = parseFloat(lower.replace('kg', '').trim());
    return isNaN(val) ? 1000 : Math.round(val * 1000);
  }
  if (lower.includes('gm') || lower.includes('g')) {
    const val = parseFloat(lower.replace(/gm|g/, '').trim());
    return isNaN(val) ? 100 : Math.round(val);
  }
  const val = parseFloat(lower);
  return isNaN(val) ? 100 : Math.round(val);
}

// Format grams into readable string (e.g. 500 g, 2.5 kg)
function formatWeightGrams(grams: number): string {
  if (grams >= 1000) {
    const kg = (grams / 1000).toFixed(2);
    return `${kg.replace(/\.00$/, '')} kg`;
  }
  return `${Math.round(grams)} g`;
}

// Component for the Overall Product Requirements printable sheet
const ProductRequirementReport = React.forwardRef(({
  requirements,
  facilityName,
  facilityCode,
  totalOrdersCount,
  activeFilterLabel
}: {
  requirements: any[];
  facilityName: string;
  facilityCode?: string;
  totalOrdersCount: number;
  activeFilterLabel: string;
}, ref: any) => {
  const totalUnits = requirements.reduce((acc, r) => acc + (Number(r.totalQuantity) || 0), 0);
  const totalWeightGrams = requirements.reduce((acc, r) => acc + (Number(r.totalWeightGrams) || 0), 0);
  const totalShortages = requirements.filter(r => r.hasShortage).length;

  return (
    <div ref={ref} className="bg-white text-black p-8 font-sans w-full max-w-[8.5in] mx-auto select-none print:p-4">
      {/* Header */}
      <div className="border-b-4 border-black pb-4 mb-4">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-black uppercase tracking-tight text-black">AJJAS SPICE OPS</h1>
            <h2 className="text-xs font-black uppercase tracking-widest text-slate-700 mt-0.5">
              Overall Product Processing & Batch Picking Requisition
            </h2>
            <p className="text-xs font-bold text-slate-800 mt-1">
              Facility: <span className="font-black text-black">{facilityName}</span> {facilityCode ? `(${facilityCode})` : ''}
            </p>
          </div>
          <div className="text-right">
            <span className="inline-block bg-black text-white px-2 py-0.5 text-[9px] font-black uppercase tracking-wider rounded">
              WAREHOUSE REQUISITION
            </span>
            <p className="text-[10px] font-bold text-slate-700 mt-1">
              Date: {format(new Date(), 'dd-MMM-yyyy HH:mm')}
            </p>
            <p className="text-[10px] font-bold text-slate-700">
              Orders Scope: {activeFilterLabel} ({totalOrdersCount} orders)
            </p>
          </div>
        </div>
      </div>

      {/* KPI Metrics */}
      <div className="grid grid-cols-4 gap-2 mb-4 border-2 border-black p-2.5 bg-slate-50 text-xs">
        <div>
          <span className="block text-[8px] font-black uppercase text-slate-500">Target Orders</span>
          <span className="text-base font-black text-black">{totalOrdersCount} Orders</span>
        </div>
        <div>
          <span className="block text-[8px] font-black uppercase text-slate-500">Products / SKUs</span>
          <span className="text-base font-black text-black">{requirements.length} Products</span>
        </div>
        <div>
          <span className="block text-[8px] font-black uppercase text-slate-500">Total Units to Pick</span>
          <span className="text-base font-black text-black">{totalUnits} Units</span>
        </div>
        <div>
          <span className="block text-[8px] font-black uppercase text-slate-500">Total Net Mass</span>
          <span className="text-base font-black text-black">{formatWeightGrams(totalWeightGrams)}</span>
        </div>
      </div>

      {totalShortages > 0 && (
        <div className="mb-3 p-2 border-2 border-red-600 bg-red-50 text-red-900 text-[11px] font-bold flex items-center justify-between">
          <span>SHORTAGE ALERT: {totalShortages} product(s) have insufficient stock at this facility!</span>
          <span className="text-[9px] uppercase font-black bg-red-600 text-white px-1.5 py-0.5 rounded">Restock Needed</span>
        </div>
      )}

      {/* Requisition Table */}
      <table className="w-full text-left border-collapse border-2 border-black mb-6 text-xs">
        <thead>
          <tr className="bg-black text-white">
            <th className="p-2 text-center w-10 text-[9px] font-black uppercase border-r border-white/20">Picked</th>
            <th className="p-2 text-[9px] font-black uppercase border-r border-white/20">Product & Category</th>
            <th className="p-2 text-[9px] font-black uppercase text-center border-r border-white/20">Pack Size</th>
            <th className="p-2 text-[9px] font-black uppercase text-center border-r border-white/20">Required</th>
            <th className="p-2 text-[9px] font-black uppercase text-center border-r border-white/20">Total Mass</th>
            <th className="p-2 text-[9px] font-black uppercase text-center border-r border-white/20">In Stock</th>
            <th className="p-2 text-[9px] font-black uppercase text-center border-r border-white/20">Stock Status</th>
            <th className="p-2 text-[9px] font-black uppercase">Order Numbers</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-black/30">
          {requirements.length === 0 ? (
            <tr>
              <td colSpan={8} className="p-6 text-center text-slate-500 font-bold">
                No product requirements found for selected order filters.
              </td>
            </tr>
          ) : (
            requirements.map((req, idx) => (
              <tr key={req.key || idx} className={req.hasShortage ? "bg-red-50/70" : idx % 2 === 1 ? "bg-slate-50" : "bg-white"}>
                <td className="p-2 text-center border-r border-black/30">
                  <div className="w-4 h-4 border-2 border-black mx-auto rounded-sm"></div>
                </td>
                <td className="p-2 font-bold text-black border-r border-black/30">
                  <div className="leading-tight text-xs font-black">{req.productName}</div>
                  <div className="text-[9px] font-semibold text-slate-500 uppercase">{req.category}</div>
                </td>
                <td className="p-2 text-center font-extrabold border-r border-black/30">
                  {req.variantWeight}
                </td>
                <td className="p-2 text-center font-black text-sm border-r border-black/30">
                  {req.totalQuantity}
                </td>
                <td className="p-2 text-center font-bold border-r border-black/30">
                  {formatWeightGrams(req.totalWeightGrams)}
                </td>
                <td className="p-2 text-center font-extrabold border-r border-black/30">
                  {req.currentStock}
                </td>
                <td className="p-2 text-center font-black text-[9px] uppercase border-r border-black/30">
                  {req.hasShortage ? (
                    <span className="text-red-700 font-black">SHORT ({req.shortageAmount})</span>
                  ) : (
                    <span className="text-emerald-700 font-black">SUFFICIENT</span>
                  )}
                </td>
                <td className="p-2 text-[9px] font-mono leading-tight text-slate-700">
                  {req.orderNumbers.slice(0, 10).join(', ')}
                  {req.orderNumbers.length > 10 && ` (+${req.orderNumbers.length - 10} more)`}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Signatures */}
      <div className="border-t-2 border-black pt-4 grid grid-cols-2 gap-8 text-xs">
        <div>
          <p className="text-[9px] font-black uppercase text-slate-500 mb-6">Picker / Warehouse Runner</p>
          <div className="border-b border-black w-48 mb-1"></div>
          <p className="text-[9px] font-bold text-slate-600">Signature & Date: ____________________</p>
        </div>
        <div className="text-right">
          <p className="text-[9px] font-black uppercase text-slate-500 mb-6">Facility Supervisor Verification</p>
          <div className="border-b border-black w-48 ml-auto mb-1"></div>
          <p className="text-[9px] font-bold text-slate-600">Signature & Date: ____________________</p>
        </div>
      </div>

      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 10mm;
          }
          body {
            background-color: #ffffff !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>
    </div>
  );
});

// Component for Bulk Slips Print Sheet (each order slip on its own page)
const BulkSlipsPrintSheet = React.forwardRef(({
  orders,
  slipType
}: {
  orders: any[];
  slipType: 'receipt' | 'label' | 'invoice';
}, ref: any) => {
  if (!orders || orders.length === 0) {
    return <div ref={ref} className="p-4 bg-white text-black font-sans">No orders selected for printing.</div>;
  }

  return (
    <div ref={ref} className="bg-white text-black">
      {orders.map((ord, idx) => (
        <div 
          key={ord.id || idx} 
          className="bulk-slip-item"
          style={{
            pageBreakAfter: idx === orders.length - 1 ? 'auto' : 'always',
            breakAfter: idx === orders.length - 1 ? 'auto' : 'page',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: slipType === 'receipt' ? '0' : '10px'
          }}
        >
          {slipType === 'receipt' && (
            <ShippingReceipt order={ord} items={ord.items || []} />
          )}
          {slipType === 'label' && (
            <ShippingLabel order={ord} items={ord.items || []} />
          )}
          {slipType === 'invoice' && (
            <Invoice order={ord} items={ord.items || []} />
          )}
        </div>
      ))}
      <style>{`
        @media print {
          @page {
            size: ${slipType === 'receipt' ? '3.15in auto' : slipType === 'label' ? '4in 6in' : 'A4 portrait'};
            margin: ${slipType === 'receipt' ? '0' : slipType === 'label' ? '0' : '10mm'};
          }
          .bulk-slip-item {
            page-break-after: always !important;
            break-after: page !important;
          }
          .bulk-slip-item:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }
        }
      `}</style>
    </div>
  );
});

export default function FacilityDashboard() {
  const { user, isStaff, isAdmin, staffInfo, logout, loading } = useAuth();
  const [, setLocation] = useLocation();
  const [allFacilities, setAllFacilities] = useState<any[]>([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>('');
  const [unit, setUnit] = useState<any>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [inventory, setInventory] = useState<any[]>([]);
  const [inventorySearch, setInventorySearch] = useState('');
  const [activeView, setActiveView] = useState<'orders' | 'ready_for_shipping' | 'completed' | 'stock' | 'reports'>('orders');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [localWeights, setLocalWeights] = useState<Record<string, Record<number, string>>>({});
  const [isPickingOrder, setIsPickingOrder] = useState<string | null>(null);

  // Reports state & filters
  const [reportsSubTab, setReportsSubTab] = useState<'requirements' | 'bulk_slips'>('requirements');
  const [requirementsFilter, setRequirementsFilter] = useState<'active' | 'Received' | 'Picking & Packing' | 'Ready for Dispatch' | 'all'>('active');
  const [requirementsSearch, setRequirementsSearch] = useState('');
  const [bulkSlipType, setBulkSlipType] = useState<'receipt' | 'label' | 'invoice'>('receipt');
  const [bulkStatusFilter, setBulkStatusFilter] = useState<string>('all');
  const [bulkOrderSearch, setBulkOrderSearch] = useState<string>('');
  const [selectedBulkOrderIds, setSelectedBulkOrderIds] = useState<string[]>([]);

  // Active facility ID resolution
  const activeFacilityId = selectedFacilityId || staffInfo?.facilityCenterId || (allFacilities.length > 0 ? allFacilities[0].id : '');

  // Refs for printing
  const labelRef = useRef<HTMLDivElement>(null);
  const invoiceRef = useRef<HTMLDivElement>(null);
  const bulkLabelsRef = useRef<HTMLDivElement>(null);
  const pickingReceiptRef = useRef<HTMLDivElement>(null);
  const requirementReportRef = useRef<HTMLDivElement>(null);
  const bulkSlipsRef = useRef<HTMLDivElement>(null);

  const [activePrintType, setActivePrintType] = useState<'label' | 'invoice' | 'receipt' | 'bulk' | null>(null);

  const handlePrintLabel = useReactToPrint({
    contentRef: labelRef,
    onAfterPrint: () => setActivePrintType(null),
  });

  const handlePrintInvoice = useReactToPrint({
    contentRef: invoiceRef,
    onAfterPrint: () => setActivePrintType(null),
  });

  const handlePrintBulk = useReactToPrint({
    contentRef: bulkLabelsRef,
    onAfterPrint: () => setActivePrintType(null),
  });

  const handlePrintPickingReceipt = useReactToPrint({
    contentRef: pickingReceiptRef,
    onAfterPrint: () => setActivePrintType(null),
  });

  const handlePrintRequirementReport = useReactToPrint({
    contentRef: requirementReportRef,
    onAfterPrint: () => {},
  });

  const handlePrintBulkSlips = useReactToPrint({
    contentRef: bulkSlipsRef,
    onAfterPrint: () => {},
  });

  const printLabel = () => {
    setActivePrintType('label');
    setTimeout(() => {
      handlePrintLabel();
    }, 250);
  };

  const printInvoice = () => {
    setActivePrintType('invoice');
    setTimeout(() => {
      handlePrintInvoice();
    }, 250);
  };

  const printBulk = () => {
    setActivePrintType('bulk');
    setTimeout(() => {
      handlePrintBulk();
    }, 250);
  };

  const printPickingReceipt = () => {
    setActivePrintType('receipt');
    setTimeout(() => {
      handlePrintPickingReceipt();
    }, 250);
  };

  const triggerPrintRequirementReport = () => {
    if (requirementsList.length === 0) {
      toast.error('No product requirements found to print.');
      return;
    }
    handlePrintRequirementReport();
  };

  const triggerPrintBulkSlips = () => {
    if (ordersForBulkPrint.length === 0) {
      toast.error('Please select at least one order to bulk print slips.');
      return;
    }
    handlePrintBulkSlips();
  };

  const handleOrderPicked = async (orderId: string) => {
    try {
      setIsPickingOrder(orderId);
      const order = orders.find(o => o.id === orderId);
      if (!order) return;

      const items = order.items || [];
      const orderLocal = localWeights[orderId] || {};
      const orderDb = order.actualWeights || {};
      const weights = { ...orderDb, ...orderLocal };

      // Ensure weights are resolved. If an item weight has not been manually entered, auto-fill with item.quantity
      const finalWeights: Record<number, string> = { ...weights };
      for (let i = 0; i < items.length; i++) {
        const val = finalWeights[i];
        if (val === undefined || val === '' || isNaN(Number(val))) {
          finalWeights[i] = String(items[i].quantity);
        }
      }

      // 1. Move status to 'Ready for Dispatch'
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, { 
        status: 'Ready for Dispatch',
        pickedAt: serverTimestamp(),
        actualWeights: finalWeights
      });

      // Update local state so progress circle instantly updates to 100%
      setLocalWeights(prev => ({
        ...prev,
        [orderId]: finalWeights
      }));

      // 2. Reduce stock from facility inventory AND catalog product stock
      const facilityId = order.facilityCenterId || activeFacilityId;

      if (items.length > 0) {
        for (const item of items) {
          const prodId = item.productId || item.id;
          if (!prodId) continue;

          const matchedProduct = products.find(p => p.id === prodId);
          const variantWeight = item.selectedWeight || (matchedProduct?.variants?.[0]?.weight) || '1 Unit';
          const reduceQty = Number(item.quantity) || 1;

          // A) Reduce from facility inventory
          if (facilityId) {
            const invId = `${prodId}_${variantWeight.replace(/\s+/g, '_')}_${facilityId}`;
            const fallbackInvId = `${prodId}_${facilityId}`;

            // Check current stock from loaded inventory state or direct doc
            const invItem = inventory.find(inv => 
              inv.productId === prodId && 
              inv.facilityCenterId === facilityId && 
              (inv.variantWeight === variantWeight || !inv.variantWeight)
            );

            let currentInvStock = invItem ? Number(invItem.stock) || 0 : 0;
            let targetInvId = invId;

            try {
              const directDoc = await getDoc(doc(db, 'inventory', invId));
              if (directDoc.exists()) {
                currentInvStock = Number(directDoc.data().stock) || 0;
                targetInvId = invId;
              } else {
                const fallbackDoc = await getDoc(doc(db, 'inventory', fallbackInvId));
                if (fallbackDoc.exists()) {
                  currentInvStock = Number(fallbackDoc.data().stock) || 0;
                  targetInvId = fallbackInvId;
                }
              }
            } catch (invReadErr) {
              console.warn("Direct inv read warning:", invReadErr);
            }

            const newInvStock = Math.max(0, currentInvStock - reduceQty);

            await setDoc(doc(db, 'inventory', targetInvId), {
              productId: prodId,
              facilityCenterId: facilityId,
              variantWeight,
              stock: newInvStock,
              updatedAt: serverTimestamp()
            }, { merge: true });

            if (newInvStock < 10) {
              createNotification(
                'LOW STOCK ALERT',
                `Critical Alert: ${item.name} (${variantWeight}) stock in "${unit?.name || 'facility'}" is low (${newInvStock} Units remaining).`,
                'inventory',
                prodId
              ).catch(console.warn);
            }
          }

          // B) Reduce global catalog stock in 'products' collection
          try {
            const productRef = doc(db, 'products', prodId);
            const productSnap = await getDoc(productRef);
            if (productSnap.exists()) {
              const pData = productSnap.data();
              const currentProdStock = Number(pData.stock) || 0;
              const newProdStock = Math.max(0, currentProdStock - reduceQty);

              let updatedVariants = pData.variants;
              if (Array.isArray(updatedVariants) && updatedVariants.length > 0) {
                updatedVariants = updatedVariants.map((v: any) => {
                  if (v.weight === variantWeight) {
                    const vStock = Number(v.stock) || 0;
                    return { ...v, stock: Math.max(0, vStock - reduceQty) };
                  }
                  return v;
                });
              }

              await updateDoc(productRef, {
                stock: newProdStock,
                isOutOfStock: newProdStock <= 0,
                ...(updatedVariants ? { variants: updatedVariants } : {}),
                updatedAt: serverTimestamp()
              });
            }
          } catch (prodErr) {
            console.warn("Product catalog stock update note:", prodErr);
          }
        }
      }

      // 3. Create picked notification
      createNotification(
        'Order Picked & Stock Reduced',
        `Order #${order.orderNumber || orderId.slice(0, 8)} marked as PICKED. Stock reduced across inventory.`,
        'status',
        orderId
      ).catch(console.warn);

      toast.success(`Order #${order.orderNumber || orderId.slice(0, 8)} marked as picked & stock updated!`);
      setSelectedOrderId(null);
    } catch (e: any) {
      console.error("Error picking order:", e);
      toast.error(`Failed to mark order as picked: ${e?.message || 'Error updating stock'}`);
    } finally {
      setIsPickingOrder(null);
    }
  };

  const handleDirectPrintShippingReceipt = (e: React.MouseEvent, orderObj: any) => {
    e.stopPropagation();
    setSelectedOrderId(orderObj.id);
    setActivePrintType('receipt');
    setTimeout(() => {
      handlePrintPickingReceipt();
    }, 250);
  };

  useEffect(() => {
    if (isScanning) {
      const scanner = new Html5QrcodeScanner(
        "dashboard-reader",
        { fps: 10, qrbox: { width: 250, height: 250 } },
        false
      );

      scanner.render((decodedText) => {
        try {
          const data = JSON.parse(decodedText);
          if (data.type === 'product') {
            setInventorySearch(data.id);
            setIsScanning(false);
            scanner.clear();
          }
        } catch (e) {
          // If not JSON, maybe it's just the ID
          setInventorySearch(decodedText);
          setIsScanning(false);
          scanner.clear();
        }
      }, () => {});

      return () => {
        scanner.clear().catch(e => console.error(e));
      };
    }
  }, [isScanning]);

  useEffect(() => {
    if (!loading && (!user || (!isStaff && !isAdmin))) {
      setLocation('/facility-login');
    }
  }, [user, isStaff, isAdmin, loading, setLocation]);

  // Load all facility centers so user/admin can operate
  useEffect(() => {
    const unsubCenters = onSnapshot(collection(db, 'facilityCenters'), (snapshot) => {
      const centers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllFacilities(centers);
      if (centers.length > 0 && !selectedFacilityId) {
        const preferred = staffInfo?.facilityCenterId || centers[0].id;
        setSelectedFacilityId(preferred);
      }
    });
    return () => unsubCenters();
  }, [staffInfo]);

  useEffect(() => {
    if (activeFacilityId) {
      const unsubCenter = onSnapshot(doc(db, 'facilityCenters', activeFacilityId), (doc) => {
        if (doc.exists()) {
          setUnit({ id: doc.id, ...doc.data() });
        } else if (allFacilities.length > 0) {
          setUnit(allFacilities[0]);
        }
      });

      const qOrders = query(
        collection(db, 'orders'), 
        where('facilityCenterId', '==', activeFacilityId),
        orderBy('createdAt', 'desc')
      );
      const unsubOrders = onSnapshot(qOrders, (snapshot) => {
        setOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data({ serverTimestamps: 'estimate' }) })));
      }, (err) => console.warn("Orders listener note:", err));

      const qProducts = query(collection(db, 'products'));
      const unsubProducts = onSnapshot(qProducts, (snapshot) => {
        setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data({ serverTimestamps: 'estimate' }) })));
      });

      const qInventory = query(collection(db, 'inventory'), where('facilityCenterId', '==', activeFacilityId));
      const unsubInventory = onSnapshot(qInventory, (snapshot) => {
        setInventory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data({ serverTimestamps: 'estimate' }) })));
      });

      return () => {
        unsubCenter();
        unsubOrders();
        unsubProducts();
        unsubInventory();
      };
    }
  }, [activeFacilityId, allFacilities]);

  const handleAdjustStock = async (productId: string, variantWeight: string, amount: number, productName: string) => {
    if (!activeFacilityId) {
      toast.error('No active facility center selected');
      return;
    }
    
    const matchedInv = inventory.find(inv => 
      inv.productId === productId && 
      (inv.variantWeight === variantWeight || !inv.variantWeight)
    );
    const currentStock = matchedInv ? (Number(matchedInv.stock) || 0) : 0;
    const newStock = Math.max(0, currentStock + amount);
    const reason = amount > 0 ? 'Stock Addition' : 'Stock Consumption/Correction';
    
    try {
      const invId = `${productId}_${variantWeight.replace(/\s+/g, '_')}_${activeFacilityId}`;
      await setDoc(doc(db, 'inventory', invId), {
        productId,
        facilityCenterId: activeFacilityId,
        variantWeight,
        stock: newStock,
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Synchronize product stock in catalog
      try {
        const prodRef = doc(db, 'products', productId);
        const prodSnap = await getDoc(prodRef);
        if (prodSnap.exists()) {
          const pData = prodSnap.data();
          const prodCurrentStock = Number(pData.stock) || 0;
          const newProdStock = Math.max(0, prodCurrentStock + amount);

          let updatedVariants = pData.variants;
          if (Array.isArray(updatedVariants) && updatedVariants.length > 0) {
            updatedVariants = updatedVariants.map((v: any) => {
              if (v.weight === variantWeight) {
                const vStock = Number(v.stock) || 0;
                return { ...v, stock: Math.max(0, vStock + amount) };
              }
              return v;
            });
          }

          await updateDoc(prodRef, {
            stock: newProdStock,
            isOutOfStock: newProdStock <= 0,
            ...(updatedVariants ? { variants: updatedVariants } : {}),
            updatedAt: serverTimestamp()
          });
        }
      } catch (prodErr) {
        console.warn("Catalog sync warning:", prodErr);
      }

      // Create Audit Log
      createNotification(
        'Inventory Adjustment',
        `${productName} (${variantWeight}) stock adjusted by unit "${unit?.name || 'facility'}". ${currentStock} -> ${newStock} (${amount > 0 ? '+' : ''}${amount} Units). Reason: ${reason}`,
        'inventory',
        productId
      ).catch(console.warn);

      toast.success(`${productName} (${variantWeight}) stock updated to ${newStock} Units`);
    } catch (e: any) {
      console.error(e);
      toast.error(`Failed to update inventory: ${e?.message || 'Error saving stock'}`);
    }
  };

  const handleUpdateStatus = async (orderId: string, currentStatus: string) => {
    const nextStatusMap: Record<string, string> = {
      'Received': 'Picking & Packing',
      'Picking & Packing': 'Ready for Dispatch',
      'Ready for Dispatch': 'Completed'
    };

    const nextStatus = nextStatusMap[currentStatus] || 'Completed';

    try {
      await updateDoc(doc(db, 'orders', orderId), { status: nextStatus });
      
      const order = orders.find(o => o.id === orderId);
      createNotification(
        'Unit Update: Order Status',
        `Unit "${unit?.name}" updated order #${order?.orderNumber || orderId.slice(0,8)} to: ${nextStatus}`,
        'status',
        orderId
      ).catch(console.warn);
      toast.success(`Order status updated to: ${nextStatus}`);
    } catch (e: any) {
      console.error(e);
      toast.error(`Failed to update status: ${e?.message || 'Error'}`);
    }
  };

  const handleWeightChange = async (orderId: string, itemIdx: number, weight: string) => {
    // 1. Update local state instantly so typing is instantly reflected
    setLocalWeights(prev => ({
      ...prev,
      [orderId]: {
        ...(prev[orderId] || {}),
        [itemIdx]: weight
      }
    }));

    // 2. Perform background write to Firestore
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        [`actualWeights.${itemIdx}`]: weight
      });
    } catch (e) {
      console.error("Error saving weight:", e);
    }
  };

  const calculateOrderProgress = (order: any) => {
    if (!order || !order.items || order.items.length === 0) return 0;
    const orderLocal = localWeights[order.id] || {};
    const orderDb = order.actualWeights || {};
    const weights = { ...orderDb, ...orderLocal };
    
    const matchedCount = order.items.filter((item: any, idx: number) => {
      const val = weights[idx];
      return val !== undefined && val !== '' && !isNaN(Number(val)) && Number(val) === Number(item.quantity);
    }).length;

    return Math.round((matchedCount / order.items.length) * 100);
  };

  const markAsReturned = async (orderId: string) => {
    if (!window.confirm('Mark this order as Returned?')) return;
    try {
      await updateDoc(doc(db, 'orders', orderId), { status: 'Returned' });
      
      const order = orders.find(o => o.id === orderId);
      createNotification(
        'Unit Alert: Order Returned',
        `Order #${order?.orderNumber || orderId.slice(0,8)} marked as RETURNED by unit: ${unit?.name}`,
        'status',
        orderId
      ).catch(console.warn);
      toast.success('Order marked as Returned');
    } catch (e: any) {
      console.error(e);
      toast.error(`Failed to mark as returned: ${e?.message || 'Error'}`);
    }
  };

  const activeUnit = unit || (allFacilities.find(f => f.id === activeFacilityId)) || allFacilities[0] || { name: 'Fulfillment Hub 1', id: activeFacilityId || 'hub_1' };

  if (loading && !activeUnit) return <div className="min-h-screen flex items-center justify-center font-bold text-slate-600">Loading fulfillment unit data...</div>;

  const stats = {
    received: orders.filter(o => o.status === 'Received').length,
    processing: orders.filter(o => o.status === 'Picking & Packing' || o.status === 'Ready for Dispatch').length,
    completed: orders.filter(o => o.status === 'Completed').length,
    returned: orders.filter(o => o.status === 'Returned').length
  };

  const filteredOrders = orders.filter(o => {
    if (activeView === 'orders') {
      return o.status !== 'Ready for Shipping' && o.status !== 'Ready for Dispatch' && o.status !== 'Completed' && o.status !== 'Returned';
    }
    if (activeView === 'ready_for_shipping') {
      return o.status === 'Ready for Shipping' || o.status === 'Ready for Dispatch';
    }
    if (activeView === 'completed') {
      return o.status === 'Completed';
    }
    return false;
  });

  // Target orders for overall product requirement calculation
  const reportTargetOrders = orders.filter(o => {
    if (requirementsFilter === 'active') {
      return o.status === 'Received' || o.status === 'Picking & Packing' || o.status === 'Ready for Dispatch';
    }
    if (requirementsFilter === 'all') {
      return true;
    }
    return o.status === requirementsFilter;
  });

  // Calculate consolidated requirements of each product & variant
  const rawRequirementsMap: Record<string, {
    key: string;
    productId: string;
    productName: string;
    variantWeight: string;
    category: string;
    image: string;
    totalQuantity: number;
    totalWeightGrams: number;
    orderIds: string[];
    orderNumbers: string[];
  }> = {};

  reportTargetOrders.forEach(order => {
    (order.items || []).forEach((item: any) => {
      const prodId = item.productId || item.id || 'item';
      const matchedProd = products.find(p => p.id === prodId);
      const variantWeight = item.selectedWeight || (matchedProd?.variants?.[0]?.weight) || '1 Unit';
      const key = `${prodId}_${variantWeight.replace(/\s+/g, '_')}`;
      const qty = Number(item.quantity) || 1;
      const weightInGrams = parseWeightInGrams(variantWeight) * qty;

      if (!rawRequirementsMap[key]) {
        rawRequirementsMap[key] = {
          key,
          productId: prodId,
          productName: item.name || matchedProd?.name || 'Product',
          variantWeight,
          category: matchedProd?.category || item.category || 'Spice',
          image: matchedProd?.image || item.image || "https://images.unsplash.com/photo-1596040033229-a9821ebd05ed?w=200&h=200&fit=crop",
          totalQuantity: 0,
          totalWeightGrams: 0,
          orderIds: [],
          orderNumbers: []
        };
      }

      rawRequirementsMap[key].totalQuantity += qty;
      rawRequirementsMap[key].totalWeightGrams += weightInGrams;
      if (!rawRequirementsMap[key].orderIds.includes(order.id)) {
        rawRequirementsMap[key].orderIds.push(order.id);
        const orderNum = order.orderNumber || order.id.slice(0, 6);
        rawRequirementsMap[key].orderNumbers.push(`#${orderNum}`);
      }
    });
  });

  const requirementsList = Object.values(rawRequirementsMap)
    .map(req => {
      const invItem = inventory.find(inv => 
        inv.productId === req.productId && 
        (inv.variantWeight === req.variantWeight || !inv.variantWeight)
      );
      const currentStock = invItem ? (Number(invItem.stock) || 0) : 0;
      const hasShortage = currentStock < req.totalQuantity;
      const shortageAmount = hasShortage ? req.totalQuantity - currentStock : 0;

      return {
        ...req,
        currentStock,
        hasShortage,
        shortageAmount
      };
    })
    .filter(req => {
      if (!requirementsSearch) return true;
      const term = requirementsSearch.toLowerCase();
      return (
        req.productName.toLowerCase().includes(term) ||
        req.category.toLowerCase().includes(term) ||
        req.variantWeight.toLowerCase().includes(term)
      );
    })
    .sort((a, b) => {
      if (a.hasShortage && !b.hasShortage) return -1;
      if (!a.hasShortage && b.hasShortage) return 1;
      return b.totalQuantity - a.totalQuantity;
    });

  // Orders filtered for Bulk Printing
  const filteredOrdersForBulk = orders.filter(o => {
    if (bulkStatusFilter !== 'all' && o.status !== bulkStatusFilter) {
      return false;
    }
    if (bulkOrderSearch) {
      const q = bulkOrderSearch.toLowerCase();
      const matchNum = (o.orderNumber || o.id || '').toLowerCase().includes(q);
      const matchCust = (o.customerName || '').toLowerCase().includes(q);
      const matchPhone = (o.customerPhone || '').toLowerCase().includes(q);
      if (!matchNum && !matchCust && !matchPhone) return false;
    }
    return true;
  });

  const ordersForBulkPrint = orders.filter(o => selectedBulkOrderIds.includes(o.id));

  const toggleSelectBulkOrder = (id: string) => {
    setSelectedBulkOrderIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAllFilteredBulk = () => {
    const allIds = filteredOrdersForBulk.map(o => o.id);
    setSelectedBulkOrderIds(allIds);
  };

  const handleDeselectAllBulk = () => {
    setSelectedBulkOrderIds([]);
  };

  const handleExportRequirementsCSV = () => {
    if (requirementsList.length === 0) {
      toast.error('No requirements to export.');
      return;
    }
    const headers = ['Product Name', 'Category', 'Pack Size', 'Required Units', 'Total Weight (g)', 'Facility Stock', 'Stock Status', 'Order Numbers'];
    const rows = requirementsList.map(item => [
      `"${item.productName.replace(/"/g, '""')}"`,
      `"${item.category || ''}"`,
      `"${item.variantWeight}"`,
      item.totalQuantity,
      item.totalWeightGrams,
      item.currentStock,
      item.hasShortage ? `SHORTAGE (-${item.shortageAmount})` : 'SUFFICIENT',
      `"${item.orderNumbers.join(', ')}"`
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Product_Requirements_${unit?.name || 'Facility'}_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Requirements exported to CSV');
  };

  const selectedOrder = filteredOrders.find(o => o.id === selectedOrderId) || filteredOrders[0];

  return (
    <div className="min-h-screen bg-[#f1f5f9] flex overflow-hidden font-sans">

      {/* Hidden Print Content (Always mounted for perfect SVG/Barcode initialization, hidden on screen, visible during printing) */}
      <div className="print-only">
        {selectedOrder && (
          <>
            <ShippingLabel ref={labelRef} order={selectedOrder} items={selectedOrder.items || []} />
            <Invoice ref={invoiceRef} order={selectedOrder} items={selectedOrder.items || []} />
            <ShippingReceipt ref={pickingReceiptRef} order={selectedOrder} items={selectedOrder.items || []} />
          </>
        )}
        <div ref={bulkLabelsRef}>
          {orders.filter(o => o.status === 'Ready for Dispatch').map(o => (
            <div key={o.id} className="mb-8 break-after-page text-black bg-white">
              <ShippingLabel order={o} items={o.items || []} />
            </div>
          ))}
        </div>

        {/* Printable Overall Product Requirement Sheet */}
        <ProductRequirementReport 
          ref={requirementReportRef}
          requirements={requirementsList}
          facilityName={activeUnit?.name || 'Central Fulfillment Hub'}
          facilityCode={activeUnit?.code || activeUnit?.pincode}
          totalOrdersCount={reportTargetOrders.length}
          activeFilterLabel={
            requirementsFilter === 'active' ? 'Active Processing (Received, Picking, Ready)' :
            requirementsFilter === 'all' ? 'All Orders' : `${requirementsFilter} Orders`
          }
        />

        {/* Printable Bulk Slips Sheet */}
        <BulkSlipsPrintSheet 
          ref={bulkSlipsRef}
          orders={ordersForBulkPrint.length > 0 ? ordersForBulkPrint : filteredOrdersForBulk}
          slipType={bulkSlipType}
        />

        <style>{`
          @media screen {
            .print-only {
              position: absolute !important;
              left: -9999px !important;
              top: -9999px !important;
              visibility: hidden !important;
            }
          }
          @media print {
            .print-only {
              position: static !important;
              visibility: visible !important;
            }
          }
        `}</style>
      </div>
      {/* Dark Sidebar (Left) */}
      <aside className="w-[72px] bg-[#0c1619] flex flex-col items-center py-6 shrink-0 transition-all print:hidden">
        <div className="w-10 h-10 bg-[#10b981] rounded-xl flex items-center justify-center mb-10 shadow-lg shadow-emerald-500/20">
          <div className="w-5 h-5 border-2 border-white rounded-sm rotate-45 flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-full"></div>
          </div>
        </div>

        <nav className="flex-1 flex flex-col gap-6">
          {[
            { id: 'orders', label: 'Orders', icon: ClipboardList },
            { id: 'ready_for_shipping', label: 'Ready for Shipping', icon: Truck },
            { id: 'completed', label: 'Completed Orders', icon: CheckCircle2 },
            { id: 'stock', label: 'Stock', icon: Package },
            { id: 'reports', label: 'Reports', icon: FileSpreadsheet }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setActiveView(item.id as any);
                setSelectedOrderId(null);
              }}
              className={cn(
                "w-12 h-12 flex items-center justify-center rounded-xl transition-all relative group",
                activeView === item.id 
                  ? "bg-[#10b981] text-black shadow-lg shadow-emerald-500/30" 
                  : "text-slate-500 hover:text-white hover:bg-white/5"
              )}
              title={item.label}
            >
              <item.icon className="w-5 h-5" strokeWidth={2.5} />
              {activeView === item.id && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white rounded-r-full" />
              )}
            </button>
          ))}
        </nav>

        <div className="flex flex-col gap-6 mt-auto">
          <button 
            onClick={logout}
            className="w-12 h-12 flex items-center justify-center rounded-xl text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <LogOut className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden print:hidden">
        {/* Top Navigation Bar */}
        <header className="h-[72px] bg-white border-b border-slate-200 flex items-center justify-between px-8 shrink-0">
          <div className="flex items-center gap-10">
            <div className="flex flex-col">
              <h1 className="text-lg font-black text-slate-900 leading-none">SpiceOps</h1>
              <p className="text-[11px] font-bold text-emerald-600 uppercase tracking-wider mt-0.5 flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                {activeUnit?.name || 'Fulfillment Center'}
              </p>
            </div>

            {allFacilities.length > 1 && (
              <div className="hidden sm:flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200">
                <Building2 className="w-3.5 h-3.5 text-emerald-600" />
                <select
                  value={activeFacilityId}
                  onChange={(e) => setSelectedFacilityId(e.target.value)}
                  className="bg-transparent text-xs font-bold text-slate-800 outline-none cursor-pointer"
                >
                  {allFacilities.map((f: any) => (
                    <option key={f.id} value={f.id}>
                      {f.name} ({f.code || f.pincode})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                type="text" 
                placeholder="Search orders..." 
                className="w-80 pl-11 pr-4 py-2.5 bg-slate-100 border-none rounded-lg text-xs font-bold text-slate-900 focus:bg-white focus:ring-2 focus:ring-slate-900/5 transition-all outline-none"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
             <div className="flex items-center gap-2">
                <button className="p-2 text-slate-400 hover:text-slate-900 transition-colors relative">
                  <Bell className="w-5 h-5" />
                  <span className="absolute top-2 right-2 w-1.5 h-1.5 bg-red-500 rounded-full border-2 border-white"></span>
                </button>
                <button 
                  onClick={() => window.location.reload()}
                  className="p-2 text-slate-400 hover:text-slate-900 transition-colors group relative"
                >
                  <RotateCcw className="w-5 h-5 group-active:rotate-180 transition-transform" />
                  <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[8px] font-black px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    REFRESH SYNC
                  </div>
                </button>
             </div>

             <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white shadow-sm ring-1 ring-slate-100 transition-transform active:scale-95 cursor-pointer">
               <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop" alt="User" />
             </div>
          </div>
        </header>

        {/* Workspace Body */}
        <div className="flex-1 flex overflow-hidden">
          {/* Order Queue (Left) */}
          {activeView !== 'stock' && activeView !== 'reports' && (
            <aside className="w-[300px] bg-white border-r border-slate-200 flex flex-col shrink-0">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-base font-black text-slate-900 tracking-tight">Queue</h2>
                <span className="bg-slate-900 text-white text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
                  {filteredOrders.length} Orders
                </span>
              </div>

              <div className="flex-1 overflow-y-auto no-scrollbar py-2 px-3 space-y-2 bg-slate-50/50">
                {filteredOrders.map((order) => {
                  const isActive = (selectedOrderId === order.id) || (!selectedOrderId && filteredOrders.indexOf(order) === 0);
                  const progress = calculateOrderProgress(order);
                  
                  return (
                    <div 
                      key={order.id}
                      onClick={() => setSelectedOrderId(order.id)}
                      className={cn(
                        "w-full text-left p-3.5 rounded-xl transition-all border flex flex-col gap-2 relative overflow-hidden group cursor-pointer",
                        isActive 
                          ? "bg-[#eafafa] border-emerald-100 shadow-md shadow-emerald-900/5" 
                          : "bg-white border-slate-100 hover:border-slate-200 shadow-sm"
                      )}
                    >
                      {isActive && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-400" />
                      )}
                      
                      <div className="flex items-start gap-3 justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-0.5">
                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-none">
                              #{order.orderNumber || order.id.slice(0, 4)}
                            </span>
                            <span className={cn(
                              "px-1.5 py-0.5 rounded text-[7px] font-black uppercase tracking-widest",
                              order.priority === 'High' ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"
                            )}>
                              {order.priority || 'Normal'}
                            </span>
                          </div>
                          
                          <h3 className="text-xs font-black text-slate-900 mb-0.5 truncate group-hover:text-emerald-600 transition-colors">
                            {order.customerName}
                          </h3>
                          
                          <p className="text-[10px] font-bold text-slate-400 leading-none flex items-center gap-1">
                            <span>{order.items?.length || 0} items</span>
                            <span>•</span>
                            <span className="text-[9px] text-slate-500 lowercase">({order.status})</span>
                          </p>
                        </div>

                        <div className="relative w-8 h-8 flex-shrink-0">
                          <svg className="w-full h-full -rotate-90">
                            <circle cx="16" cy="16" r="13" className="fill-none stroke-slate-100" strokeWidth="3" />
                            <motion.circle 
                              cx="16" cy="16" r="13" 
                              className={cn("fill-none", isActive ? "stroke-emerald-400" : "stroke-slate-200")} 
                              strokeWidth="3" 
                              strokeDasharray="81.68"
                              initial={{ strokeDashoffset: 81.68 }}
                              animate={{ strokeDashoffset: 81.68 - (81.68 * progress) / 100 }}
                              transition={{ duration: 1, ease: "easeOut" }}
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center text-[8px] font-black text-slate-900">
                            {progress}%
                          </div>
                        </div>
                      </div>

                      {/* Micro Order Action Bar */}
                      <div className="flex items-center gap-2 mt-1 pt-2 border-t border-slate-105">
                        {activeView === 'orders' ? (
                          <button
                            disabled={isPickingOrder === order.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOrderPicked(order.id);
                            }}
                            className="flex-1 py-1.5 px-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-[9px] font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1 shadow-sm shadow-emerald-500/15"
                            style={{ color: '#ffffff' }}
                          >
                            <CheckCircle2 className={cn("w-3 h-3 text-white", isPickingOrder === order.id && "animate-spin")} style={{ color: '#ffffff' }} />
                            <span className="text-white !text-white" style={{ color: '#ffffff' }}>
                              {isPickingOrder === order.id ? 'Updating Stock...' : 'Order Picked'}
                            </span>
                          </button>
                        ) : order.status === 'Completed' ? (
                          <div 
                            className="flex-1 py-1.5 px-2.5 bg-emerald-600 text-white rounded-lg text-[9px] font-black uppercase tracking-wider flex items-center justify-center gap-1 shadow-sm"
                            style={{ color: '#ffffff' }}
                          >
                            <CheckCircle2 className="w-3 h-3 text-white" style={{ color: '#ffffff' }} />
                            <span className="text-white !text-white" style={{ color: '#ffffff' }}>Completed Order</span>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateStatus(order.id, order.status);
                            }}
                            className="flex-1 py-1.5 px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[9px] font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1 shadow-sm"
                            style={{ color: '#ffffff' }}
                          >
                            <CheckCircle2 className="w-3 h-3 text-white" style={{ color: '#ffffff' }} />
                            <span className="text-white !text-white" style={{ color: '#ffffff' }}>Complete Order</span>
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDirectPrintShippingReceipt(e, order)}
                          className="p-1.5 bg-slate-150 hover:bg-emerald-50 text-slate-600 hover:text-emerald-700 rounded-lg transition-all"
                          title="Print Shipping Receipt"
                        >
                          <Printer className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </aside>
          )}

          {/* Active Processing / Content Area (Right) */}
          <div className="flex-1 overflow-y-auto no-scrollbar bg-[#f8fafc]">
             {activeView === 'orders' || activeView === 'ready_for_shipping' || activeView === 'completed' ? (
                <div className="p-6">
                   {selectedOrder ? (
                        <div className="max-w-4xl mx-auto space-y-5">
                          {/* Active Order Header */}
                          <div className="flex justify-between items-end">
                             <div className="space-y-0.5">
                                <div className="flex items-center gap-2 text-emerald-500 font-black text-[9px] uppercase tracking-widest">
                                   <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                                   Active Processing
                                </div>
                                <h1 className="text-xl font-black text-slate-900">
                                   {selectedOrder.customerName} <span className="text-slate-300 font-bold ml-2">#{selectedOrder.orderNumber || selectedOrder.id.slice(0, 4)}</span>
                                </h1>
                             </div>

                             <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-slate-200 shadow-sm">
                                <button 
                                  onClick={() => setSelectedOrderId(null)}
                                  className="p-2 text-slate-400 hover:text-slate-900 transition-colors"
                                >
                                   <X className="w-4 h-4" />
                                </button>
                                <div className="w-px h-4 bg-slate-100 mx-1" />
                                <button className="p-2 text-slate-400 hover:text-slate-900 transition-colors">
                                   <ChevronRight className="w-4 h-4" />
                                </button>
                             </div>
                          </div>

                          {/* Stats Grid */}
                          <div className="grid grid-cols-4 gap-4">
                             {[
                               { label: 'Status', value: selectedOrder.status || 'Weight Needed', icon: Gauge, color: 'text-indigo-600', bg: 'bg-indigo-50/50' },
                               { label: 'Priority', value: selectedOrder.priority || 'Normal', icon: AlertTriangle, color: selectedOrder.priority === 'High' ? 'text-red-500' : 'text-amber-500', bg: selectedOrder.priority === 'High' ? 'bg-red-50/50' : 'bg-amber-50/50', iconPrefix: selectedOrder.priority === 'High' ? '! ' : '' },
                               { label: 'Assigned', value: 'Marcus', icon: User, color: 'text-slate-900', bg: 'bg-slate-50' },
                               { label: 'Deadline', value: '14:00 PM', icon: Clock, color: 'text-emerald-600', bg: 'bg-emerald-50/50' }
                             ].map((stat, i) => (
                               <div key={i} className={cn("p-3.5 rounded-xl border border-slate-200 bg-white transition-all hover:shadow-md hover:shadow-slate-900/5 group", stat.bg)}>
                                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{stat.label}</p>
                                  <div className="flex items-center gap-2">
                                     <stat.icon className={cn("w-4 h-4", stat.color)} />
                                     <span className={cn("text-xs font-black text-slate-900", stat.color.includes('red') && 'font-bold flex items-center')}>
                                        {stat.iconPrefix}{stat.value}
                                     </span>
                                  </div>
                               </div>
                             ))}
                          </div>

                          {/* Order Line Items */}
                          <div className="space-y-4 pb-12">
                             {(selectedOrder.items || []).map((item: any, i: number) => {
                                const currentWeight = localWeights[selectedOrder.id]?.[i] !== undefined
                                  ? localWeights[selectedOrder.id]?.[i]
                                  : (selectedOrder.actualWeights?.[i] || '');
                                const isMatched = currentWeight !== '' && !isNaN(Number(currentWeight)) && Number(currentWeight) === Number(item.quantity);
                                const targetVal = Number(item.quantity) || 1;
                                const enteredVal = (currentWeight !== '' && !isNaN(Number(currentWeight))) ? Number(currentWeight) : 0;
                                const percentDiff = Math.round(((enteredVal - targetVal) / targetVal) * 100);
                                const varianceText = percentDiff > 0 ? `+${percentDiff}%` : `${percentDiff}%`;
                                
                                return (
                                  <div key={i} className={cn(
                                    "p-4 rounded-xl border transition-all relative overflow-hidden",
                                    isMatched 
                                      ? "bg-[#e2f9ee]/45 border-emerald-100 ring-2 ring-emerald-500/10" 
                                      : "bg-white border-slate-200 shadow-sm"
                                  )}>
                                     <div className="flex justify-between items-start mb-3">
                                        <div className="flex gap-4">
                                           <div className="w-10 h-10 rounded-lg overflow-hidden border border-slate-100 shadow-md">
                                              {(() => {
                                                 const matchedProduct = products.find(p => p.id === item.productId || p.id === item.id);
                                                 const productImage = matchedProduct?.image || item.image || "https://images.unsplash.com/photo-1596040033229-a9821ebd05ed?w=200&h=200&fit=crop";
                                                 return (
                                                    <img 
                                                       src={productImage} 
                                                       alt={item.name} 
                                                       referrerPolicy="no-referrer" 
                                                       className="w-full h-full object-cover" 
                                                    />
                                                 );
                                              })()}
                                           </div>
                                           <div>
                                              <h4 className="text-sm font-black text-slate-900 leading-tight mb-0.5">{item.name}</h4>
                                              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                                                 SKU: {item.sku || 'N/A'} • Batch #4492
                                              </p>
                                           </div>
                                        </div>

                                        <div className="text-right">
                                           <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Target</p>
                                           <p className="text-lg font-black text-slate-900">
                                              {item.quantity}.00 <span className="text-slate-400 font-bold text-xs uppercase ml-0.5">{item.unit || 'kg'}</span>
                                           </p>
                                        </div>
                                     </div>

                                     <div className="space-y-1.5">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider ml-1">Actual Weight (kg)</p>
                                        <div className="flex gap-4">
                                           <div className={cn(
                                             "flex-1 h-14 border rounded-xl flex items-center px-4 relative group transition-all",
                                             isMatched ? "bg-white border-emerald-500 ring-1 ring-emerald-500/20" : "bg-slate-50 border-slate-200"
                                           )}>
                                              <input 
                                                 className="w-full bg-transparent border-none outline-none text-xl font-black text-slate-900 placeholder:text-slate-300" 
                                                 value={currentWeight}
                                                 onChange={(e) => handleWeightChange(selectedOrder.id, i, e.target.value)}
                                                 placeholder="0.00"
                                              />
                                              
                                              {isMatched ? (
                                                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                                                   <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-white shadow-md shadow-emerald-500/20">
                                                      <CheckCircle2 className="w-3.5 h-3.5" />
                                                   </div>
                                                   <span className="text-emerald-600 text-[8px] font-black uppercase tracking-widest">Matched</span>
                                                </div>
                                              ) : (
                                                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-3">
                                                   <div className="text-right">
                                                      <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-0.5">Variance</p>
                                                      <p className="text-sm font-black text-red-500">{varianceText}</p>
                                                   </div>
                                                </div>
                                              )}
                                           </div>
                                        </div>
                                     </div>
                                  </div>
                                );
                             })}
                          </div>
                        </div>
                   ) : (
                     <div className="h-full flex flex-col items-center justify-center text-center">
                        <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center shadow-xl shadow-slate-200 mb-8 border border-slate-100">
                           <Package className="w-12 h-12 text-slate-200" />
                        </div>
                        <h3 className="text-xl font-black text-slate-900 mb-2">No Order Selected</h3>
                        <p className="text-slate-400 text-sm font-medium">Select an operation from the queue to begin processing.</p>
                     </div>
                   )}
                </div>
             ) : activeView === 'stock' ? (
                <div className="p-10 max-w-6xl mx-auto">
                   <div className="flex justify-between items-end mb-10">
                      <div>
                         <h2 className="text-3xl font-black text-slate-900">Inventory Registry</h2>
                         <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mt-1">Real-time stock monitoring & adjustments</p>
                      </div>
                      <button 
                        onClick={() => setIsScanning(true)}
                        className="bg-white border border-slate-200 p-4 rounded-2xl flex items-center gap-3 text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm"
                      >
                         <QrCode className="w-5 h-5 text-emerald-500" /> Scan QR
                      </button>
                   </div>

                   <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-xl shadow-slate-900/5 overflow-hidden">
                      <table className="w-full text-left">
                         <thead className="bg-slate-50/50 border-b border-slate-100">
                            <tr>
                               <th className="px-10 py-6 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Product Detail</th>
                               <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Warehouse Level</th>
                               <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] text-right">Stock Control</th>
                            </tr>
                         </thead>
                         <tbody className="divide-y divide-slate-100">
                            {products
                              .filter(p => !inventorySearch || p.name.toLowerCase().includes(inventorySearch.toLowerCase()))
                              .flatMap(product => {
                                const variants = product.variants && product.variants.length > 0 
                                  ? product.variants 
                                  : [{ weight: product.category === 'whole' ? '100g' : '1 Unit', price: product.price }];
                                
                                return variants.map(v => ({
                                  ...product,
                                  subVariant: v
                                }));
                              })
                              .map(item => {
                                const variantWeight = item.subVariant.weight;
                                const matchedInv = inventory.find(inv => inv.productId === item.id && inv.variantWeight === variantWeight);
                                const unitStock = matchedInv ? matchedInv.stock : 0;
                                const lowStockLimit = 10;
                                
                                return (
                                  <tr key={`${item.id}_${variantWeight}`} className="hover:bg-slate-50/50 transition-colors">
                                     <td className="px-10 py-6">
                                        <div className="flex items-center gap-6">
                                           <div className="w-14 h-14 rounded-xl border border-slate-100 overflow-hidden shadow-sm">
                                              <img 
                                                 src={item.subVariant.image || item.image || "https://images.unsplash.com/photo-1596040033229-a9821ebd05ed?w=200&h=200&fit=crop"} 
                                                 referrerPolicy="no-referrer" 
                                                 className="w-full h-full object-cover" 
                                              />
                                           </div>
                                           <div>
                                              <p className="font-black text-slate-900">{item.name}</p>
                                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{item.category} • {variantWeight}</p>
                                           </div>
                                        </div>
                                     </td>
                                     <td className="px-8 py-6">
                                        <div className={cn(
                                          "inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-black",
                                          unitStock < lowStockLimit ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"
                                        )}>
                                           {unitStock} <span className="text-[10px] uppercase font-bold opacity-70">Units</span>
                                        </div>
                                     </td>
                                     <td className="px-8 py-6 text-right">
                                        <div className="flex justify-end items-center gap-3">
                                           <button 
                                              onClick={() => handleAdjustStock(item.id, variantWeight, -1, item.name)}
                                              className="w-10 h-10 border border-slate-200 rounded-xl flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all"
                                              title="Adjust -1 Unit"
                                           >
                                              <Minus className="w-5 h-5" />
                                           </button>
                                           <div className="w-16 h-10 bg-slate-50 rounded-xl flex items-center justify-center font-black text-slate-900 border border-slate-100">
                                              {unitStock}
                                           </div>
                                           <button 
                                              onClick={() => handleAdjustStock(item.id, variantWeight, 1, item.name)}
                                              className="w-10 h-10 border border-slate-200 rounded-xl flex items-center justify-center text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 transition-all"
                                              title="Adjust +1 Unit"
                                           >
                                              <Plus className="w-5 h-5" />
                                           </button>
                                        </div>
                                     </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                       </table>
                   </div>
                   
                   {isScanning && (
                      <div className="fixed inset-0 bg-[#0c1619]/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                        <div className="bg-white w-full max-w-md p-10 shadow-2xl rounded-[3rem] text-center">
                          <h3 className="text-xl font-black text-slate-900 mb-2 uppercase tracking-tight">Laser Grid Scan</h3>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-8">Place QR code within scanning brackets</p>
                          <div id="dashboard-reader" className="overflow-hidden border-4 border-emerald-500/20 bg-slate-900 rounded-[2rem] aspect-square"></div>
                          <button 
                            onClick={() => setIsScanning(false)}
                            className="mt-8 px-10 py-4 bg-slate-100 text-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-red-50 hover:text-red-600 transition-all"
                          >
                            Terminate Scan
                          </button>
                        </div>
                      </div>
                    )}
                </div>
             ) : activeView === 'reports' ? (
                <div className="p-8 max-w-7xl mx-auto space-y-6">
                   {/* Reports Header & Sub-Tab Navigation */}
                   <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 pb-6">
                      <div>
                         <div className="flex items-center gap-2 text-emerald-600 font-black text-[10px] uppercase tracking-widest mb-1">
                            <FileSpreadsheet className="w-4 h-4" />
                            <span>Warehouse Intelligence & Operations</span>
                         </div>
                         <h2 className="text-3xl font-black text-slate-900 tracking-tight">Reports & Bulk Dispatch</h2>
                         <p className="text-xs font-bold text-slate-500 mt-1">
                            Consolidated production requisitions, batch picking requirements, and bulk order slip printing.
                         </p>
                      </div>

                      {/* Sub-tab pills */}
                      <div className="flex items-center gap-2 bg-slate-200/70 p-1.5 rounded-2xl border border-slate-200 shrink-0">
                         <button
                            onClick={() => setReportsSubTab('requirements')}
                            className={cn(
                               "flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black transition-all",
                               reportsSubTab === 'requirements'
                                  ? "bg-white text-slate-900 shadow-md shadow-slate-900/5"
                                  : "text-slate-600 hover:text-slate-900"
                            )}
                         >
                            <Boxes className="w-4 h-4 text-emerald-600" />
                            <span>Overall Product Requirements</span>
                            <span className="ml-1 bg-emerald-100 text-emerald-800 text-[10px] font-black px-2 py-0.5 rounded-full">
                               {requirementsList.length}
                            </span>
                         </button>
                         <button
                            onClick={() => setReportsSubTab('bulk_slips')}
                            className={cn(
                               "flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black transition-all",
                               reportsSubTab === 'bulk_slips'
                                  ? "bg-white text-slate-900 shadow-md shadow-slate-900/5"
                                  : "text-slate-600 hover:text-slate-900"
                            )}
                         >
                            <Printer className="w-4 h-4 text-emerald-600" />
                            <span>Bulk Print Order Slips</span>
                            <span className="ml-1 bg-slate-100 text-slate-700 text-[10px] font-black px-2 py-0.5 rounded-full">
                               {selectedBulkOrderIds.length > 0 ? selectedBulkOrderIds.length : filteredOrdersForBulk.length}
                            </span>
                         </button>
                      </div>
                   </div>

                   {/* Sub-Tab 1: Overall Product Requirements */}
                   {reportsSubTab === 'requirements' && (
                      <div className="space-y-6">
                         {/* Controls & Filter Bar */}
                         <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
                            <div className="flex flex-wrap items-center gap-3">
                               <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Order Scope:</span>
                                  <select
                                     value={requirementsFilter}
                                     onChange={(e) => setRequirementsFilter(e.target.value as any)}
                                     className="bg-slate-100 border border-slate-200 text-slate-800 text-xs font-bold rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-emerald-500/20"
                                  >
                                     <option value="active">Active Processing (Received, Picking, Ready)</option>
                                     <option value="Received">Received Only</option>
                                     <option value="Picking & Packing">Picking & Packing Only</option>
                                     <option value="Ready for Dispatch">Ready for Dispatch Only</option>
                                     <option value="all">All Orders (Including Completed)</option>
                                  </select>
                               </div>

                               <div className="relative">
                                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                  <input 
                                     type="text"
                                     value={requirementsSearch}
                                     onChange={(e) => setRequirementsSearch(e.target.value)}
                                     placeholder="Filter products / variant..."
                                     className="pl-8 pr-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 placeholder:text-slate-400 outline-none w-56 focus:bg-white focus:ring-2 focus:ring-emerald-500/20"
                                  />
                               </div>
                            </div>

                            <div className="flex items-center gap-3 shrink-0">
                               <button
                                  onClick={handleExportRequirementsCSV}
                                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2"
                               >
                                  <Download className="w-4 h-4 text-slate-600" />
                                  <span>Export CSV</span>
                               </button>

                               <button
                                  onClick={triggerPrintRequirementReport}
                                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white !text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 shadow-md shadow-emerald-600/20 active:scale-95"
                                  style={{ color: '#ffffff' }}
                               >
                                  <Printer className="w-4 h-4 text-white" style={{ color: '#ffffff' }} />
                                  <span className="text-white !text-white" style={{ color: '#ffffff' }}>Print Requirements Sheet</span>
                               </button>
                            </div>
                         </div>

                         {/* KPI Summary Cards */}
                         <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                               <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">Target Orders</p>
                               <p className="text-2xl font-black text-slate-900">{reportTargetOrders.length} <span className="text-xs text-slate-400 font-bold">Orders</span></p>
                            </div>
                            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                               <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">Distinct Products</p>
                               <p className="text-2xl font-black text-slate-900">{requirementsList.length} <span className="text-xs text-slate-400 font-bold">SKUs</span></p>
                            </div>
                            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                               <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">Total Units Needed</p>
                               <p className="text-2xl font-black text-emerald-600">
                                  {requirementsList.reduce((acc, r) => acc + r.totalQuantity, 0)} <span className="text-xs text-emerald-700 font-bold">Units</span>
                                </p>
                            </div>
                            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                               <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">Total Net Mass</p>
                               <p className="text-2xl font-black text-indigo-600">
                                  {formatWeightGrams(requirementsList.reduce((acc, r) => acc + r.totalWeightGrams, 0))}
                               </p>
                            </div>
                         </div>

                         {/* Shortage Warning Banner */}
                         {requirementsList.some(r => r.hasShortage) && (
                            <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-between text-amber-900">
                               <div className="flex items-center gap-3">
                                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                                  <div>
                                     <p className="text-xs font-black uppercase tracking-wide">Facility Stock Shortage Warning</p>
                                     <p className="text-[11px] font-medium text-amber-700">
                                        One or more products required for processing have less inventory in this facility than the total ordered quantity.
                                     </p>
                                  </div>
                               </div>
                               <button 
                                  onClick={() => setActiveView('stock')}
                                  className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-[10px] font-black uppercase tracking-wider transition-all shrink-0"
                               >
                                  Adjust Stock Now
                               </button>
                            </div>
                         )}

                         {/* Requirements Data Table */}
                         <div className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-slate-900/5 overflow-hidden">
                            <table className="w-full text-left">
                               <thead className="bg-slate-50/70 border-b border-slate-100">
                                  <tr>
                                     <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-wider">Product & Category</th>
                                     <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-wider text-center">Pack Size</th>
                                     <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-wider text-center">Required Qty</th>
                                     <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-wider text-center">Total Weight</th>
                                     <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-wider text-center">Facility Stock</th>
                                     <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-wider text-center">Stock Status</th>
                                     <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-wider">Associated Orders</th>
                                  </tr>
                               </thead>
                               <tbody className="divide-y divide-slate-100">
                                  {requirementsList.length === 0 ? (
                                     <tr>
                                        <td colSpan={7} className="px-6 py-12 text-center">
                                           <Boxes className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                           <p className="text-sm font-black text-slate-800">No product requirements found</p>
                                           <p className="text-xs text-slate-400 mt-1">There are no orders matching the selected scope "{requirementsFilter}".</p>
                                        </td>
                                     </tr>
                                  ) : (
                                     requirementsList.map((req) => (
                                        <tr key={req.key} className={cn("hover:bg-slate-50/60 transition-colors", req.hasShortage && "bg-red-50/20")}>
                                           <td className="px-6 py-4">
                                              <div className="flex items-center gap-3">
                                                 <div className="w-10 h-10 rounded-xl border border-slate-100 overflow-hidden shrink-0 shadow-sm">
                                                    <img 
                                                       src={req.image} 
                                                       alt={req.productName} 
                                                       referrerPolicy="no-referrer"
                                                       className="w-full h-full object-cover" 
                                                    />
                                                 </div>
                                                 <div>
                                                    <p className="font-black text-slate-900 text-xs leading-snug">{req.productName}</p>
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{req.category}</p>
                                                 </div>
                                              </div>
                                           </td>
                                           <td className="px-4 py-4 text-center">
                                              <span className="inline-block px-2.5 py-1 bg-slate-100 rounded-lg text-xs font-black text-slate-700">
                                                 {req.variantWeight}
                                              </span>
                                           </td>
                                           <td className="px-4 py-4 text-center">
                                              <span className="text-base font-black text-slate-900">
                                                 {req.totalQuantity}
                                              </span>
                                              <span className="text-[10px] font-bold text-slate-400 uppercase ml-1">Units</span>
                                           </td>
                                           <td className="px-4 py-4 text-center">
                                              <span className="text-xs font-black text-slate-700">
                                                 {formatWeightGrams(req.totalWeightGrams)}
                                              </span>
                                           </td>
                                           <td className="px-4 py-4 text-center">
                                              <span className="text-xs font-black text-slate-900">
                                                 {req.currentStock} Units
                                              </span>
                                           </td>
                                           <td className="px-4 py-4 text-center">
                                              {req.hasShortage ? (
                                                 <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase bg-red-100 text-red-700">
                                                    <AlertTriangle className="w-3 h-3" />
                                                    Short ({req.shortageAmount})
                                                 </span>
                                              ) : (
                                                 <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase bg-emerald-100 text-emerald-800">
                                                    <Check className="w-3 h-3" />
                                                    In Stock
                                                 </span>
                                              )}
                                           </td>
                                           <td className="px-6 py-4">
                                              <div className="flex flex-wrap gap-1 max-w-xs">
                                                 {req.orderNumbers.slice(0, 5).map((num, i) => (
                                                    <span key={i} className="text-[9px] font-mono font-bold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                                                       {num}
                                                    </span>
                                                 ))}
                                                 {req.orderNumbers.length > 5 && (
                                                    <span className="text-[9px] font-black text-slate-400 px-1 py-0.5">
                                                       +{req.orderNumbers.length - 5} more
                                                    </span>
                                                 )}
                                              </div>
                                           </td>
                                        </tr>
                                     ))
                                  )}
                               </tbody>
                            </table>
                         </div>
                      </div>
                   )}

                   {/* Sub-Tab 2: Bulk Print Order Slips */}
                   {reportsSubTab === 'bulk_slips' && (
                      <div className="space-y-6">
                         {/* Slip Format Selection & Filters */}
                         <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                               <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 block mb-1.5">
                                     Select Slip Print Format:
                                  </label>
                                  <div className="flex flex-wrap items-center gap-2">
                                     {[
                                        { id: 'receipt', label: 'Thermal Receipt (3.1")', desc: 'POS Roll' },
                                        { id: 'label', label: 'Shipping Label (4x6")', desc: 'Courier Dispatch' },
                                        { id: 'invoice', label: 'Packing Invoice (A4)', desc: 'Full Invoice' }
                                     ].map((fmt) => (
                                        <button
                                           key={fmt.id}
                                           onClick={() => setBulkSlipType(fmt.id as any)}
                                           className={cn(
                                              "px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 border",
                                              bulkSlipType === fmt.id
                                                 ? "bg-slate-900 text-white border-slate-900 shadow-md shadow-slate-900/10"
                                                 : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                                           )}
                                        >
                                           <span>{fmt.label}</span>
                                           <span className={cn(
                                              "text-[9px] px-1.5 py-0.2 rounded font-normal uppercase",
                                              bulkSlipType === fmt.id ? "bg-white/20 text-white" : "text-slate-400"
                                           )}>{fmt.desc}</span>
                                        </button>
                                     ))}
                                  </div>
                               </div>

                               <div className="flex items-center gap-3 shrink-0">
                                  <button
                                     onClick={triggerPrintBulkSlips}
                                     disabled={(selectedBulkOrderIds.length === 0 && filteredOrdersForBulk.length === 0)}
                                     className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white !text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 shadow-lg shadow-emerald-600/25 active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
                                     style={{ color: '#ffffff' }}
                                  >
                                     <Printer className="w-4 h-4 text-white" style={{ color: '#ffffff' }} />
                                     <span className="text-white !text-white" style={{ color: '#ffffff' }}>
                                        Bulk Print {selectedBulkOrderIds.length > 0 ? selectedBulkOrderIds.length : filteredOrdersForBulk.length} Slips
                                     </span>
                                  </button>
                               </div>
                            </div>

                            {/* Secondary filter & search bar */}
                            <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
                               <div className="flex flex-wrap items-center gap-3">
                                  <div className="flex items-center gap-2">
                                     <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Order Status:</span>
                                     <select
                                        value={bulkStatusFilter}
                                        onChange={(e) => setBulkStatusFilter(e.target.value)}
                                        className="bg-slate-100 border border-slate-200 text-slate-800 text-xs font-bold rounded-xl px-3 py-1.5 outline-none focus:ring-2 focus:ring-emerald-500/20"
                                     >
                                        <option value="all">All Statuses ({orders.length})</option>
                                        <option value="Received">Received ({orders.filter(o => o.status === 'Received').length})</option>
                                        <option value="Picking & Packing">Picking & Packing ({orders.filter(o => o.status === 'Picking & Packing').length})</option>
                                        <option value="Ready for Dispatch">Ready for Dispatch ({orders.filter(o => o.status === 'Ready for Dispatch').length})</option>
                                        <option value="Completed">Completed ({orders.filter(o => o.status === 'Completed').length})</option>
                                     </select>
                                  </div>

                                  <div className="relative">
                                     <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                     <input 
                                        type="text"
                                        value={bulkOrderSearch}
                                        onChange={(e) => setBulkOrderSearch(e.target.value)}
                                        placeholder="Search order #, customer, phone..."
                                        className="pl-8 pr-3 py-1.5 bg-slate-100 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 placeholder:text-slate-400 outline-none w-64 focus:bg-white focus:ring-2 focus:ring-emerald-500/20"
                                     />
                                  </div>
                               </div>

                               <div className="flex items-center gap-2">
                                  <button
                                     onClick={handleSelectAllFilteredBulk}
                                     className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5"
                                  >
                                     <CheckSquare className="w-3.5 h-3.5" />
                                     <span>Select All Filtered ({filteredOrdersForBulk.length})</span>
                                  </button>
                                  {selectedBulkOrderIds.length > 0 && (
                                     <button
                                        onClick={handleDeselectAllBulk}
                                        className="px-3 py-1.5 bg-slate-100 hover:bg-red-50 hover:text-red-600 text-slate-700 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5"
                                     >
                                        <Square className="w-3.5 h-3.5" />
                                        <span>Deselect All</span>
                                     </button>
                                  )}
                                  <span className="text-xs font-bold text-slate-500 ml-2">
                                     {selectedBulkOrderIds.length} of {filteredOrdersForBulk.length} selected
                                  </span>
                               </div>
                            </div>
                         </div>

                         {/* Orders List Table */}
                         <div className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-slate-900/5 overflow-hidden">
                            <table className="w-full text-left">
                               <thead className="bg-slate-50/70 border-b border-slate-100">
                                  <tr>
                                     <th className="px-4 py-4 w-10 text-center">
                                        <button 
                                           onClick={selectedBulkOrderIds.length === filteredOrdersForBulk.length && filteredOrdersForBulk.length > 0 ? handleDeselectAllBulk : handleSelectAllFilteredBulk}
                                           className="text-slate-500 hover:text-slate-900"
                                        >
                                           {selectedBulkOrderIds.length > 0 && selectedBulkOrderIds.length === filteredOrdersForBulk.length ? (
                                              <CheckSquare className="w-4 h-4 text-emerald-600" />
                                           ) : (
                                              <Square className="w-4 h-4 text-slate-400" />
                                           )}
                                        </button>
                                     </th>
                                     <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-wider">Order No</th>
                                     <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-wider">Customer Details</th>
                                     <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-wider">Items Summary</th>
                                     <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-wider text-center">Status</th>
                                     <th className="px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-wider text-right">Date</th>
                                     <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-wider text-right">Quick Print</th>
                                  </tr>
                               </thead>
                               <tbody className="divide-y divide-slate-100">
                                  {filteredOrdersForBulk.length === 0 ? (
                                     <tr>
                                        <td colSpan={7} className="px-6 py-12 text-center">
                                           <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                           <p className="text-sm font-black text-slate-800">No orders match filter criteria</p>
                                           <p className="text-xs text-slate-400 mt-1">Try adjusting your status filter or search query.</p>
                                        </td>
                                     </tr>
                                  ) : (
                                     filteredOrdersForBulk.map((order) => {
                                        const isSelected = selectedBulkOrderIds.includes(order.id);
                                        const totalQty = (order.items || []).reduce((acc: number, it: any) => acc + (Number(it.quantity) || 1), 0);
                                        
                                        return (
                                           <tr 
                                              key={order.id} 
                                              onClick={() => toggleSelectBulkOrder(order.id)}
                                              className={cn(
                                                 "hover:bg-slate-50/60 transition-colors cursor-pointer",
                                                 isSelected && "bg-emerald-50/30"
                                              )}
                                           >
                                              <td className="px-4 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                                                 <input 
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleSelectBulkOrder(order.id)}
                                                    className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                                 />
                                              </td>
                                              <td className="px-4 py-4">
                                                 <div className="flex items-center gap-2">
                                                    <span className="font-extrabold text-xs text-slate-900">
                                                       #{order.orderNumber || order.id.slice(0, 8)}
                                                    </span>
                                                    {order.priority === 'High' && (
                                                       <span className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase bg-red-100 text-red-700">
                                                          High
                                                       </span>
                                                    )}
                                                 </div>
                                              </td>
                                              <td className="px-6 py-4">
                                                 <p className="font-black text-xs text-slate-900 leading-tight">{order.customerName}</p>
                                                 <p className="text-[10px] text-slate-500 truncate max-w-xs">{order.customerAddress || 'No address'}</p>
                                                 {order.customerPhone && (
                                                    <p className="text-[9px] font-bold text-slate-400">{order.customerPhone}</p>
                                                 )}
                                              </td>
                                              <td className="px-6 py-4">
                                                 <p className="text-xs font-bold text-slate-800">
                                                    {order.items?.length || 0} Products ({totalQty} Units)
                                                 </p>
                                                 <p className="text-[10px] text-slate-400 truncate max-w-xs">
                                                    {(order.items || []).map((it: any) => `${it.name} (${it.quantity})`).join(', ')}
                                                 </p>
                                              </td>
                                              <td className="px-4 py-4 text-center">
                                                 <span className={cn(
                                                    "inline-block px-2.5 py-1 rounded-full text-[10px] font-black uppercase",
                                                    order.status === 'Completed' ? "bg-emerald-100 text-emerald-800" :
                                                    order.status === 'Ready for Dispatch' ? "bg-indigo-100 text-indigo-800" :
                                                    order.status === 'Picking & Packing' ? "bg-amber-100 text-amber-800" :
                                                    "bg-slate-100 text-slate-700"
                                                 )}>
                                                    {order.status}
                                                 </span>
                                              </td>
                                              <td className="px-4 py-4 text-right">
                                                 <p className="text-xs font-bold text-slate-700">
                                                    {order.createdAt?.toDate 
                                                       ? format(order.createdAt.toDate(), 'dd MMM, HH:mm')
                                                       : 'Recent'}
                                                 </p>
                                              </td>
                                              <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                                                 <button
                                                    onClick={(e) => handleDirectPrintShippingReceipt(e, order)}
                                                    className="p-2 bg-slate-100 hover:bg-emerald-50 text-slate-600 hover:text-emerald-700 rounded-xl transition-all"
                                                    title="Print Slip for this Order"
                                                 >
                                                    <Printer className="w-4 h-4" />
                                                 </button>
                                              </td>
                                           </tr>
                                        );
                                     })
                                  )}
                               </tbody>
                            </table>
                         </div>
                      </div>
                   )}
                </div>
             ) : (
                <div className="h-full flex flex-col items-center justify-center text-center p-10">
                   <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center shadow-xl shadow-slate-200 mb-8 border border-slate-100">
                      <LayoutDashboard className="w-12 h-12 text-slate-200" />
                   </div>
                   <h3 className="text-2xl font-black text-slate-900 mb-4 tracking-tight uppercase">Operational Grid Syncing</h3>
                   <p className="text-slate-400 font-bold uppercase tracking-[0.3em] text-[10px] max-w-sm mx-auto leading-relaxed">
                      The {activeView} module is currently performing a regional handshake with the central distribution network.
                   </p>
                </div>
             )}
          </div>
        </div>

        {/* Footer Processing Bar */}
        {activeView !== 'stock' && activeView !== 'reports' && (
          <footer className="h-16 bg-white border-t border-slate-200 px-6 flex items-center justify-between shrink-0">
             <div className="flex items-center gap-4">
                <div className="flex -space-x-2">
                   <div className="w-8 h-8 bg-indigo-100 border-2 border-white rounded-full flex items-center justify-center text-[9px] font-black text-indigo-600 shadow-sm">MV</div>
                   <div className="w-8 h-8 bg-emerald-100 border-2 border-white rounded-full flex items-center justify-center text-[9px] font-black text-emerald-600 shadow-sm">JD</div>
                </div>
                <p className="text-[10px] font-bold text-slate-500 tracking-tight">
                   <span className="text-slate-900 font-black">2 Team members</span> processing this order
                </p>
             </div>

             <div className="flex items-center gap-3">
                <button 
                  onClick={printLabel}
                  disabled={!selectedOrder}
                  className="px-5 py-2 bg-white border border-slate-200 text-[#0c1619] rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none transition-all shadow-sm"
                >
                   <div className="flex items-center gap-2">
                      <Printer className="w-3.5 h-3.5" />
                      Print Label
                   </div>
                </button>
                <button 
                  onClick={printPickingReceipt}
                  disabled={!selectedOrder}
                  className="px-5 py-2 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-emerald-100 disabled:opacity-50 disabled:pointer-events-none transition-all shadow-sm"
                >
                   <div className="flex items-center gap-2">
                      <Printer className="w-3.5 h-3.5 text-emerald-600" />
                      Print Picking Receipt
                   </div>
                </button>
                <button 
                  onClick={() => selectedOrder && handleUpdateStatus(selectedOrder.id, selectedOrder.status)}
                  disabled={!selectedOrder || selectedOrder.status === 'Completed'}
                  className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white !text-white rounded-lg text-[10px] font-black uppercase tracking-widest disabled:opacity-80 disabled:pointer-events-none transition-all shadow-md shadow-emerald-600/20 active:scale-95 flex items-center gap-2"
                  style={{ color: '#ffffff' }}
                >
                   <CheckCircle2 className="w-3.5 h-3.5 text-white" style={{ color: '#ffffff' }} />
                   <span className="text-white !text-white font-bold" style={{ color: '#ffffff' }}>
                     {selectedOrder?.status === 'Completed' ? 'Completed Order' : 'Complete Order'}
                   </span>
                </button>
             </div>
          </footer>
        )}
      </main>
    </div>
  );
}
